import { COLUMN_DEFAULTS, type TColumnElement } from '@/columnElement';
import { TRBLString, getBackgroundColorStyle, getHeightStyle, getWidthStyle, parseTRBL } from '@/utils';
import { convertToHTMLAttributes } from '@/utils/convertToHTMLAttributes';
import type { DOMOutputSpec, Fragment } from '@tiptap/pm/model';
import classnames from 'classnames';
import type { CSSProperties } from 'react';
import styles from './Columns.css';
import type { ColumnsElementProps, SortableColumn, TColumnsElement } from './Columns.type';

export function getColumnsInnerWrapperRenderHTML({ node }: { node: Node }): [string, ...any[]] {
  return [
    'div',
    convertToHTMLAttributes(
      getColumnsInnerWrapperAttributes({ element: node as unknown as TColumnsElement, children: [] }),
    ),
    0,
  ];
}

export function getColumnsElementRenderHTML({
  node,
  HTMLAttributes,
}: {
  node: Node;
  HTMLAttributes?: Record<string, any>;
}): DOMOutputSpec {
  return [...getColumnsRootRenderHTML({ node, HTMLAttributes }), [...getColumnsInnerWrapperRenderHTML({ node })]];
}

function getColumnsRootRenderHTML({
  node,
  HTMLAttributes,
}: {
  node: Node;
  HTMLAttributes?: Record<string, any>;
}): [string, ...any[]] {
  return [
    'div',
    convertToHTMLAttributes(
      getColumnsRootAttributes({
        element: node as unknown as TColumnsElement,
        children: [],
        attributes: HTMLAttributes,
      }),
    ),
  ];
}

const getMobileLayout = (mobileLayout?: string | Array<SortableColumn>): Record<string, any> => {
  if (!mobileLayout) {
    return {};
  }

  let parsedLayout: Array<SortableColumn>;
  if (typeof mobileLayout === 'string') {
    parsedLayout = JSON.parse(mobileLayout) as Array<SortableColumn>;
  } else {
    parsedLayout = mobileLayout;
  }

  if (!parsedLayout || !parsedLayout.length) {
    return {};
  }

  return parsedLayout.reduce(
    (acc, column, index) => {
      acc[`--mobile-column-order-${column.columnIndex + 1}`] = index;
      acc[`--mobile-column-display-${column.columnIndex + 1}`] = column.isHidden ? 'none' : 'flex';
      return acc;
    },
    {} as Record<string, any>,
  );
};

export function getColumnsRootAttributes(props: ColumnsElementProps) {
  const { children, ...restProps } = props;
  const { element, attributes, className } = restProps;
  const {
    mobile_direction = 'direction_vertical',
    width,
    width_max,
    customAttributes,
    padding,
    margin,
    gap,
    border_radius,
    box_shadow,
    isFullHeight,
    mobileLayout,
    backgroundColor,
    mobileGap,
    mobileMaxWidth,
  } = element.attrs || {};
  const { top, bottom } = parseTRBL(padding ?? COLUMN_DEFAULTS.padding);

  const mobilePadding = parseTRBL(padding ?? COLUMN_DEFAULTS.paddingMobile);

  const mobilePaddingString = TRBLString({
    top,
    right: mobilePadding.right,
    bottom,
    left: mobilePadding.left,
  });

  let widthStyles: CSSProperties;
  if (mobileMaxWidth) {
    widthStyles = {
      '--width': getWidthStyle(width, width_max).width,
      '--max-width': getWidthStyle(width, width_max).maxWidth,
    } as CSSProperties;
  } else {
    widthStyles = getWidthStyle(width, width_max);
  }

  return {
    ...customAttributes,
    className: classnames(className, styles.columnElement, {
      [styles.stackHorizontal]: mobile_direction === 'direction_horizontal',
      [styles.stackVertical]: mobile_direction === 'direction_vertical',
    }),
    style: {
      ...widthStyles,
      ...getBackgroundColorStyle(backgroundColor),
      '--padding': padding ?? COLUMN_DEFAULTS.padding,
      '--padding-root': COLUMN_DEFAULTS.paddingRoot,
      '--padding-mobile': mobilePaddingString,
      '--padding-root-mobile': mobilePaddingString,
      '--margin': margin ?? COLUMN_DEFAULTS.margin,
      '--gap': gap ?? COLUMN_DEFAULTS.gap,
      '--mobile-gap': mobileGap ?? gap ?? COLUMN_DEFAULTS.gap,
      '--column-gap': gap ?? COLUMN_DEFAULTS.gap,
      '--gap-mobile-vertical': gap ?? COLUMN_DEFAULTS.gapMobileVertical,
      '--border-radius': border_radius ?? COLUMN_DEFAULTS.borderRadius,
      '--box-shadow': box_shadow ?? COLUMN_DEFAULTS.boxShadow,
      '--flex-grow': isFullHeight ? 1 : undefined,
      ...getMobileLayout(mobileLayout),
      ...(mobileMaxWidth ? { '--mobile-max-width': `${mobileMaxWidth}` } : {}),
    } as CSSProperties,
    ...attributes,
  };
}

function isFragment(elementContent: TColumnElement[] | Fragment): elementContent is Fragment {
  return 'childCount' in elementContent;
}

export function getColumnsInnerWrapperAttributes(props: ColumnsElementProps) {
  const { element } = props;

  const {
    width,
    width_max,
    height,
    align,
    vertical_align,
    mobile_horizontal_overflow,
    mobile_hide,
    desktop_hide,
    gridTemplateColumns,
  } = element.attrs || {};

  let numChildren = 0;
  const elementContent = element.content;
  if (isFragment(elementContent)) {
    numChildren = elementContent.childCount;
  } else {
    numChildren = elementContent.length;
  }

  return {
    className: classnames(styles.column, {
      'mobile:hidden': mobile_hide,
      'desktop:hidden': desktop_hide,
      [styles.alignLeft]: align === 'left',
      [styles.alignCenter]: align === 'center',
      [styles.alignRight]: align === 'right',
      [styles.alignTop]: vertical_align === 'align_start',
      [styles.alignMiddle]: vertical_align === 'align_center',
      [styles.alignBottom]: vertical_align === 'align_end',
      [styles.mobileHorizontalSqueeze]: mobile_horizontal_overflow === 'squeeze',
      [styles.mobileHorizontalScroll]: mobile_horizontal_overflow === 'scroll',
    }),
    style: {
      '--min-height': getHeightStyle(height).height,
      '--width': getWidthStyle(width, width_max).width,
      '--column-width': getWidthStyle(width, width_max, {
        isScrollHorizontalOverflow: mobile_horizontal_overflow === 'scroll',
      }).width,
      '--grid-template-columns': gridTemplateColumns,
      '--num-children': numChildren,
    } as CSSProperties,
  };
}
